import React, {FormEvent, FunctionComponent} from "react";
import {useTranslation} from "react-i18next";
import {CommandBar, ICommandBarItemProps,} from "office-ui-fabric-react/lib/CommandBar";
import {useDispatch, useSelector} from "react-redux";
import {DisplayDocument} from "../../types/types";
import {
	deleteDocument,
	downloadFileInDrive,
	logout,
	refreshList,
	uploadLargeFile,
	uploadSmallFile,
	createFolder,
	createFile
} from "../../store/actions/dataActions";

// Styles
import {classNames} from "./FluentCommandBar.styles";
import {FluentDialog} from "../Modal/FluentDialog";
import {ContextualMenuItemType} from "office-ui-fabric-react";
import {useModals} from "../../context/ModalContext";
import {TextField} from "@fluentui/react";

export const FluentCommandBar: FunctionComponent = () => {
	const [inputFile, setInputFile] = React.useState<HTMLElement>();
	// const [folderName, setFolderName] = React.useState<string>("");

	const dispatch = useDispatch();
	const {t, i18n} = useTranslation();
	const {openModal, closeModal} = useModals();

	const selectedDocuments = useSelector(
		(state: any) => state.data.selectedDocuments
	);

	const createFolderSetup = () => {
		let folderName: string = "";
		openModal(FluentDialog, {
			title: `${t("create_folder")}?`,
			primaryText: t("create"),
			jsx: <TextField defaultValue={folderName}
			                onChange={(e: FormEvent, val: string) => {
				                folderName = val
			                }}
			                label={t("folder_name")} required/>,
			primaryAction: () => {
				dispatch(createFolder(folderName));
				closeModal();
			}
		})
	}

	const createFileSetup = (title: string, templatePath: string) => {
		let fileName: string = "";
		openModal(FluentDialog, {
			title: `${title}?`,
			primaryText: t("create"),
			jsx: <TextField defaultValue={fileName}
			                onChange={(e: FormEvent, val: string) => {
				                fileName = val
			                }}
			                label={t("file_name")} required/>,
			primaryAction: () => {
				dispatch(createFile(fileName, templatePath));
				closeModal();
			}
		});
	}

	const _items: ICommandBarItemProps[] = [
		{
			key: "new",
			text: t("new"),
			iconProps: {iconName: "Add"},
			subMenuProps: {
				items: [
					{
						key: "folder",
						text: t("folder"),
						iconProps: {iconName: "FabricFolder"},
						onClick: () => createFolderSetup()
					},
					{
						key: "divider",
						name: "-",
						itemType: ContextualMenuItemType.Divider
					},
					{
						key: "word",
						text: t("word"),
						iconProps: {iconName: "WordDocument"},
						onClick: () => createFileSetup(t("create_word"), "Document.docx")
					},
					{
						key: "excel",
						text: t("excel"),
						iconProps: {iconName: "ExcelDocument"},
						onClick: () => createFileSetup(t("create_excel"), "Book.xlsx")
					},
					{
						key: "powerpoint",
						text: t("powerpoint"),
						iconProps: {iconName: "PowerPointDocument"},
						onClick: () => createFileSetup(t("create_powerpoint"), "Presentation.pptx")
					},
				],
			}
		},
		{
			key: "download",
			text: t("download"),
			disabled: selectedDocuments.length < 1,
			iconProps: {iconName: "Download"},
			onClick: () => download(),
		},
		{
			key: "upload",
			text: t("upload"),
			iconProps: {iconName: "Upload"},
			onClick: () => inputFile.click(),
		},
		{
			key: "delete",
			text: t("delete"),
			iconProps: {iconName: "Delete"},
			disabled: selectedDocuments.length < 1,
			onClick: () => openDeleteDialog(),
		},
		{
			key: "refresh",
			text: t("refresh"),
			iconProps: {iconName: "Refresh"},
			onClick: () => dispatch(refreshList()),
		},
	];

	const _farItems: ICommandBarItemProps[] = [
		{
			key: "logout",
			text: t("logout"),
			iconProps: {iconName: "SignOut"},
			onClick: () => {
				dispatch(logout());
			},
		},
		{
			key: "language",
			iconOnly: true,
			iconProps: {iconName: "LocaleLanguage"},
			subMenuProps: {
				items: [
					{
						key: "no",
						text: t("no"),
						onClick: () => {
							i18n.changeLanguage("no");
						},
					},
					{
						key: "en",
						text: t("en"),
						onClick: () => {
							i18n.changeLanguage("en");
						},
					},
				],
			},
		},
	];

	const deleteSelectedFiles = () => {
		selectedDocuments.forEach((document: DisplayDocument) => {
			dispatch(deleteDocument(document.key));
		});
		closeModal();
	};

	const openDeleteDialog = () => {
		openModal(FluentDialog, {
			title: `${t("delete")}?`,
			primaryText: t("delete"),
			text: selectedDocuments.length > 1 ? t("multipleDeleteText") : t("deleteText"),
			primaryAction: deleteSelectedFiles
		})
	};

	const download = () => {
		selectedDocuments.forEach((document: DisplayDocument) => {
			if (document.fileType !== "folder" || document.downloadUrl.length > 6) {
				dispatch(downloadFileInDrive(document.downloadUrl, document.name));
			}
		});
	};

	React.useEffect(() => {
		if (inputFile) {
			inputFile.onchange = (e: any) => {
				let files: FileList = e.target.files;

				if (files.length > 0) {
					let reader = new FileReader();

					// Triggers each time the file reading operation is successfully completed
					reader.onload = (ev) => {
						console.debug(ev.target.result);
					};

					// Go through all the files that are uploaded
					Array.from(files).forEach((file: File) => {
						// If the file size is less than 4MB perform a small fil upload, otherwise create an upload session for larger files
						if (file.size < 4000000) {
							dispatch(uploadSmallFile(file));
						} else {
							dispatch(uploadLargeFile(file));
						}
					});
				}
			};
		}
	}, [inputFile, dispatch]);

	return (
		<div className={classNames.commandBarWrapper}>
			<input
				type="file"
				style={{visibility: "hidden"}}
				ref={(input) => {
					setInputFile(input);
				}}
			/>

			{/* Command Bar */}
			<CommandBar
				className={classNames.commandBar}
				items={_items}
				farItems={_farItems}
				ariaLabel="Use left and right arrow keys to navigate between commands"
			/>
		</div>
	);
};
